<script>
import RawData from "@/components/widgets/raw-data.vue";
import DatePicker from "vue2-datepicker";
import { required } from "vuelidate/lib/validators";

export default {
    name: "StockLedger",
    components: {
        RawData,
        DatePicker
    },
    props: {
        stocks: {
            type: Array,
            default: () => [],
        },
    },
    validations: {
        inventoryDate: { required },
    },
    data() {
        return {
            currentPage: 1,
            perPage: 10,
            pageOptions: [10, 25, 50, 100],
            filter: "",
            sortBy: "id",
            sortDesc: false,
            fields: [
                {
                    key: "select",
                    label: "",
                    thStyle: { width: "50px" }
                },
                {
                    key: "stockNumber",
                    sortable: true,
                    label: "Stock Number"
                },
                {
                    key: "name",
                    sortable: true,
                    label: "Stock Name"
                },
                {
                    key: "itemCategory",
                    sortable: true,
                    label: "Category"
                },
                {
                    key: "unit",
                    sortable: false,
                    label: "Unit"
                },
                {
                    key: "available",
                    sortable: false,
                    label: "Current Balance",
                    thStyle: { width: "150px" },
                    tdClass: "text-center v-align-middle"
                },
            ],
            tableData: [],
            tableBusy: false,
            inventoryDate: new Date(),
            submitted: false,
        }
    },
    computed: {
        filtered() {
            let data = this.tableData;
            if (this.filter.trim() != "" && this.filter) {
                data = data.filter((item) => {
                const searchTerm = this.filter.toUpperCase();
                    return (
                        item.name.toUpperCase().includes(searchTerm) ||
                        item.stockNumber.toUpperCase().includes(searchTerm) ||
                        item.description.toUpperCase().includes(searchTerm) ||
                        item.itemCategory.toUpperCase().includes(searchTerm)
                    );
                });
            }
            return data;
        },
        rows() {
            return this.filtered.length;
        },
        isAllSelected: {
            get() {
                return this.tableData.length > 0 && this.tableData.every(item => item.selected);
            },
            set(value) {
                this.tableData.forEach(item => {
                    item.selected = value;
                });
            }
        },
        selectedItems() {
            return this.tableData.filter(item => item.selected);
        }
    },
    methods: {
        resetData(data) {
            this.inventoryDate = new Date();
            let ids = data.map(item => item.stockItemId);
            let items = this.stocks.filter(item => !ids.includes(item.id));
            this.tableData = JSON.parse(JSON.stringify(items));
        },
        submitData() {
            this.submitted = true;
            this.$v.inventoryDate.$touch();
            if (this.$v.inventoryDate.$invalid) return;
            let data = {
                inventoryDate: this.inventoryDate,
                stockItemIds: this.selectedItems.map(item => item.id)
            }
            this.$emit('submit', data);
        },
    }
}
</script>
<template>
    <b-modal
        id="stock-inventory-modal"
        title="Stock List"
        size="xl"
        title-class="font-18"
        hide-footer
        no-close-on-backdrop
    >
        <div class="row mt-1">
            <div class="col-sm-12 col-md-6">
                <div id="tickets-table_filter" class="dataTables_filter">
                    <label class="d-inline-flex align-items-center">
                        Search:
                        <b-form-input
                            autocomplete="off"
                            v-model="filter"
                            type="search"
                            placeholder="Search..."
                            class="form-control form-control-sm ms-2"
                        ></b-form-input>
                    </label>
                </div>
            </div>
            <div class="col-sm-12 col-md-6"  v-if="rows > perPage">
                <div class="dataTables_paginate paging_simple_numbers float-end">
                    <ul class="pagination pagination-rounded mb-0 pagination-small">
                    <b-pagination
                        v-model="currentPage"
                        :total-rows="rows"
                        :per-page="perPage"
                    ></b-pagination>
                    </ul>
                </div>
            </div>
        </div>
        <div class="custom-table-responsive mb-0 mt-2 border-top">
            <b-table
                :items="filtered"
                :fields="fields"
                :current-page="currentPage"
                :per-page="perPage"
                :busy="tableBusy"
                striped
                hover
                responsive
                show-empty
                empty-text="No stocks found"
            >
                <!-- Select All Checkbox -->
                <template #head(select)>
                    <div class="form-check form-checkbox-outline form-check-success mb-0">
                        <input
                            type="checkbox"
                            class="form-check-input"
                            v-model="isAllSelected"
                        />
                    </div>
                </template>
                <template #cell(select)="data">
                    <div class="form-check form-checkbox-outline form-check-success mb-0">
                    <input
                        type="checkbox"
                        class="form-check-input"
                        v-model="data.item.selected"
                    />
                    </div>
                </template>
                <template #cell(stockNumber)="data">
                    <div class="position-relative">
                        <RawData :data="data.item" v-if="$store.state.decrypt.isAdmin()" />
                        <a href="javascript:void(0);" class="text-primary hover-underline">{{data.value}}</a>
                    </div>
                </template>
                <template #cell(name)="data">
                    <div>
                        {{data.value}}
                        <small class="d-block"><i>{{data.item.description}}</i></small>
                    </div>
                </template>
                <template #cell(available)="data">
                    <h5 class="mb-0">{{data.value}}</h5>
                </template>
            </b-table>
        </div>
        <div class="d-flex justify-content-between align-items-center">
            <div class="d-flex gap-1 align-items-center">
                <label for="inventoryDate" class="no-wrap mb-0 text-white font-size-14">Inventory Date:</label>
                <date-picker
                    id="inventoryDate"
                    v-model="inventoryDate"
                    :first-day-of-week="1"
                    lang="en"
                    placeholder="Enter Inventory Date..."
                    :class="{ 'is-invalid': submitted && $v.inventoryDate.$error }"
                >
                </date-picker>
                <div
                    v-if="submitted && $v.inventoryDate.$error"
                    class="invalid-feedback"
                >
                    <span v-if="!$v.inventoryDate.required">This value is required.</span>
                </div>  
            </div>
            <div class="d-flex gap-1">
                <button type="button" class="btn btn-success" v-if="selectedItems.length" @click="submitData"><i class="bx bx-check me-1"></i>Add Selected ({{ selectedItems.length }})</button>
                <button type="button" class="btn btn-link" @click="$bvModal.hide('stock-inventory-modal')">Close</button>
            </div>
        </div>
    </b-modal>
</template>